// The sidebar pieces that make the web read like the Mac for the same account
// (ios/Sources/Views/TeamsHeaderStrip.swift, ios/SourcesMac/MacChannelSidebar.swift):
// - TeamStrip: muted teams DIMMED with no badge, a right-click menu to silence/lift a team, the
//   badge on the current team too (the Mac draws it on every tile).
// - FoldedCategories: collapsed categories with nothing to surface fold into one row of pills.
// - CategoryHeader: the muted bell on a muted category, the aggregate mention badge while collapsed.
// - RowMuteItem + MuteConfirm: the row's quick mute (confirmation first, like the Mac's alert).
import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { AvatarIdentity } from '../../../core/avatarIdentity';
import { categoryDisplayName } from '../../../core/channelListDerivation';
import { sectionMentionCount, type ChannelListSection, type DisplayChannel } from '../../../core/channelListDisplayModels';
import { ChannelMute } from '../../../core/channelMute';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Button, Dialog } from '../../kit/Kit';
import { mediaUrls } from '../../media';
import type { TeamTile } from '../../models/sidebarModel';
import { ShellContext } from '../../shell/ShellContext';
import './syncParity.css';

function useToast(): (text: string) => void {
  const shell = useContext(ShellContext);
  return (text) => shell?.toast(text, 'error');
}

function useDismiss(open: boolean, close: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const later = setTimeout(() => {
      window.addEventListener('click', close);
      window.addEventListener('contextmenu', close);
    });
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', close);
    return () => {
      clearTimeout(later);
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', close);
    };
  }, [open, close]);
}

export function TeamStrip({ teams, current, onSelect, isAdmin, serverBase }: { teams: TeamTile[]; current: string | null; onSelect(id: string): void; isAdmin: boolean; serverBase: string }) {
  const [menu, setMenu] = useState<{ x: number; y: number; tile: TeamTile } | null>(null);
  const toast = useToast();
  const close = () => setMenu(null);
  useDismiss(!!menu, close);
  const toggle = async (tile: TeamTile) => {
    setMenu(null);
    const ok = await data.call('setTeamMuted', tile.team.id, !tile.isMuted).catch(() => false);
    if (!ok) toast(t('teams.mute.failed'));
  };
  return (
    <div className="team-strip">
      {teams.length > 1
        ? teams.map((tile) => {
            const { team, mentionCount, hasUnread, isMuted } = tile;
            return (
              <button
                key={team.id}
                className={`team-tile${isMuted ? ' muted' : ''}`}
                aria-current={team.id === current}
                title={team.displayName}
                aria-label={isMuted ? `${team.displayName} · ${t('channelList.muted.accessibilityLabel')}` : team.displayName}
                onClick={() => onSelect(team.id)}
                // Right-click a team to silence it — where a desktop user looks for a per-item setting.
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenu({ x: e.clientX, y: e.clientY, tile });
                }}
              >
                <span className="team-tile-face">
                  {team.lastTeamIconUpdate > 0 ? (
                    <img src={mediaUrls.teamIcon(team.id, team.lastTeamIconUpdate)} alt="" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
                  ) : (
                    AvatarIdentity.initials(team.displayName)
                  )}
                </span>
                {/* The badge rides OUTSIDE the dimmed logo; a silenced team has none (the model zeroes it). */}
                {mentionCount > 0 ? <span className="badge">{mentionCount > 99 ? '99+' : mentionCount}</span> : hasUnread ? <span className="dot" /> : null}
              </button>
            );
          })
        : null}
      {isAdmin && teams.length > 1 ? <span className="strip-hairline" aria-hidden /> : null}
      {isAdmin ? (
        // The System Console closes the row after a hairline, the same 32 px tile a team gets
        // (TeamsHeaderStrip): the gear must not read as one more team.
        <a className="strip-gear" href={`${serverBase}/admin_console`} target="_blank" rel="noreferrer" title={t('mac.systemConsole.help')} aria-label={t('mac.systemConsole')}>
          <Icon name="gear" size={17} />
        </a>
      ) : null}
      {menu
        ? createPortal(
            <div className="popover sync-parity-menu" style={{ left: menu.x, top: menu.y }} role="menu" onClick={(e) => e.stopPropagation()}>
              <button className="menu-item" role="menuitem" data-testid="team-mute-toggle" onClick={() => void toggle(menu.tile)}>
                <Icon name={menu.tile.isMuted ? 'bell' : 'bellSlash'} size={16} /> {menu.tile.isMuted ? t('teams.unmute') : t('teams.mute')}
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** One row of pills for the collapsed categories with nothing to surface; a click opens one. */
export function FoldedCategories({ sections, onExpand }: { sections: ChannelListSection[]; onExpand(id: string): void }) {
  if (!sections.length) return null;
  return (
    <div className="folded-categories">
      {sections.map((s) => (
        <button key={s.id} className={`folded-category${s.isMuted ? ' muted' : ''}`} onClick={() => onExpand(s.id)} aria-expanded={false}>
          <span className="folded-name">{categoryDisplayName(s.type, s.displayName)}</span>
          <span className="folded-count">{s.rows.length}</span>
          <Icon name="chevronRight" size={10} strokeWidth={2.4} />
        </button>
      ))}
    </div>
  );
}

/** The header as the Mac draws it: muted glyph on a muted category, mentions while collapsed. */
export function CategoryHeader({ section, onToggle }: { section: ChannelListSection; onToggle(): void }) {
  const mentions = section.collapsed ? sectionMentionCount(section) : 0;
  return (
    <button className={`category-header${section.isMuted ? ' muted' : ''}`} aria-expanded={!section.collapsed} onClick={onToggle}>
      <Icon name="chevronDown" size={12} strokeWidth={2} />
      <span className="category-header-name">{categoryDisplayName(section.type, section.displayName)}</span>
      {section.isMuted ? <Icon name="bellSlash" size={11} title={t('channelList.muted.accessibilityLabel')} /> : null}
      {mentions > 0 ? <span className="badge category-header-badge">{mentions}</span> : null}
    </button>
  );
}

/** The row menu's mute entry. Muting asks first (the Mac's alert, 2026-10-08); lifting does not. */
export function RowMuteItem({ row, onDone, onAskConfirm }: { row: DisplayChannel; onDone(): void; onAskConfirm(row: DisplayChannel): void }) {
  const toast = useToast();
  return (
    <button
      className="menu-item"
      role="menuitem"
      data-testid="row-mute-toggle"
      onClick={() => {
        onDone();
        if (row.isMuted) void applyRowMute(row, false, toast);
        else onAskConfirm(row);
      }}
    >
      <Icon name={row.isMuted ? 'bell' : 'bellSlash'} size={16} /> {row.isMuted ? t('channelNotifications.unmute') : t('channelNotifications.mute')}
    </button>
  );
}

async function applyRowMute(row: DisplayChannel, muted: boolean, toast: (s: string) => void): Promise<void> {
  // Only `mark_unread` goes to the server: the push level and desktop settings stay as they are.
  const ok = await data.call('setChannelNotifyProps', row.id, ChannelMute.notifyProps(muted)).catch(() => false);
  if (!ok) toast(t('channelNotifications.muteFailed'));
}

export function MuteConfirm({ row, onClose }: { row: DisplayChannel; onClose(): void }) {
  const toast = useToast();
  return (
    <Dialog
      title={tf('channelNotifications.muteConfirm.title', row.name)}
      onClose={onClose}
      width={420}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <span data-testid="row-mute-confirm" style={{ display: 'contents' }}>
            <Button
              kind="danger"
              onClick={() => {
                onClose();
                void applyRowMute(row, true, toast);
              }}
            >
              {t('channelNotifications.muteConfirm.action')}
            </Button>
          </span>
        </>
      }
    >
      <p className="sync-parity-confirm">{t('channelNotifications.muteConfirm.message')}</p>
    </Dialog>
  );
}
